import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AgGridColumnProps,
  Button,
  Paper,
  DropdownMenu,
  FieldLabel,
  DataGrid,
} from '@roc/ui';
import { RETRACTED_STAGE } from '../constants/payoffs';
import { MoreVert } from '@material-ui/icons';
import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { formatDate, getPayoffId } from '@roc/feature-utils';
import { usePayoffRequestStore } from '../hooks/usePayoffRequestStore';
import { useBaseStore } from '@roc/feature-app-core';
import { useHistory, useLocation } from 'react-router';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { useCommunicationRoutesConfig } from '@roc/feature-communication';
import { RetractPayoffDialog } from './retractPayoffDialog';
import { PayoffDetailsDialog } from './payoffDetailsDialog';

const noOpComparator = () => 0;

const useStyles = makeStyles(theme => ({
  paper: {
    padding: 24,
  },
  link: {
    textDecoration: 'underline',
    color: 'rgba(66, 132, 243, 1)',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    }
  },
}));

export const LoanPayoffs = observer(({ loanId }: { loanId: string }) => {
  const classes = useStyles();

  const {
    payoffRequestStore,
    payoffRequestStore: { loanPayoffs, currentPayoff },
  } = usePayoffRequestStore();
  const { globalStore } = useBaseStore();

  const { pathname } = useLocation();
  const { push } = useHistory();
  const { loanRoutesConfig } = useLoanRoutes();
  const { communicationRoutesConfig } = useCommunicationRoutesConfig();

  useEffect(() => {
    payoffRequestStore.getActivePayoffDataByLoanId(loanId);
    payoffRequestStore.getPayoffsByLoanId(loanId);
  }, [loanId, payoffRequestStore]);

  useEffect(() => {
    if (currentPayoff) {
      payoffRequestStore.getPayoffsByLoanId(loanId);
    }
  }, [currentPayoff, payoffRequestStore, loanId]);

  useEffect(() => {
    const getPayOffIdNumberFromURL = (pathname) => {
      const payoffIdURL = getPayoffId(pathname);
      const payoffIdNumber = Number(payoffIdURL);
      return (payoffIdNumber && !isNaN(payoffIdNumber)) ? payoffIdNumber : null;
    }

    if (!globalStore.isInternalPortal) {
      const payoffIdNumber = getPayOffIdNumberFromURL(pathname);

      if (payoffIdNumber) {
        payoffRequestStore.setSelectedPayoffId(payoffIdNumber);
        payoffRequestStore.setIsPayoffDetailsModalOpen(true);
      }
    }
  }, [pathname, payoffRequestStore, globalStore.isInternalPortal]);

  const payoffColumns: AgGridColumnProps[] = [
    {
      field: 'payoffNumber',
      headerName: 'Payoff #',
      minWidth: 100,
      cellRenderer: 'payoffNumberCellRenderer',
      comparator: noOpComparator,
      sortable: false,
    },
    {
      field: 'payoffStatus',
      headerName: 'Status',
      minWidth: 100,
      comparator: noOpComparator,
      sortable: false,
    },
    {
      field: 'requestedDate',
      headerName: 'Requested Date',
      minWidth: 100,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      sortable: false,
    },
    {
      field: 'goodThroughDate',
      headerName: 'Payoff Date',
      minWidth: 100,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      sortable: false,
    },
    {
      field: 'requestedByFullName',
      headerName: 'Requested By',
      minWidth: 100,
      comparator: noOpComparator,
      sortable: false,
    },
    {
      field: 'loanPayoffDataId',
      headerName: 'Actions',
      minWidth: 50,
      cellRenderer: 'actionCellRenderer',
    },
  ];

  const frameworkComponents = {
    payoffNumberCellRenderer: params => {
      const payoffId = params.node.data?.loanPayoffDataId;
      return <Button variant='text' className={classes.link} unsetTextTransfrom onClick={() => openPayoffDetails(payoffId)} testId='open-payoff'>{`Payoff ${params.node.data?.payoffNumber}`}</Button>
    },
    dateCellRenderer: ({ value }) => {
      return value ? formatDate(value, 'MM/dd/yyyy') : '';
    },
    actionCellRenderer: params => {
      const data = params.node.data;
      return (
        <>
          {data?.payoffStatus !== RETRACTED_STAGE &&
            <DropdownMenu options={getActions(data)}>
              <MoreVert />
            </DropdownMenu>
          }
        </>
      );
    },
  };

  const getActions = (data) => {
    const actionList = [];
    if (data?.payoffStatus !== RETRACTED_STAGE) {
      actionList.push(
        {
          name: 'Retract Payoff',
          action: () => openRetractPayoff(data),
        }
      );
    }
    /*
    actionList.push(
      {
        name: 'Contact Payoff Specialist',
        action: () => openConversationsDialog(),
      }
    );
    */
    return actionList;
  };

  const openRetractPayoff = (payoff) => {
    if (payoff.payoffStatus !== RETRACTED_STAGE)
      payoffRequestStore.setIsNewPayoffRetractModalOpen(true);
  };

  const openConversationsDialog = () => {
    push(communicationRoutesConfig.conversations(loanId).url, { from: pathname });
  }

  const openPayoffDetails = (payoffId: number) => {
    if (globalStore.isInternalPortal || globalStore.isBorrowerPortal) {
      payoffRequestStore.setSelectedPayoffId(payoffId);
      payoffRequestStore.setIsPayoffDetailsModalOpen(true);
    } else {
      push(loanRoutesConfig.loans(loanId).children.payoffDetails(`${payoffId}`).url);
    }
  }

  return (
    <>
      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid container xs={12} md={12} direction='row' justifyContent='space-between'>
            <FieldLabel
            >
              Payoffs
            </FieldLabel>
          </Grid>
          <Grid item xs={12} md={12}>
            <DataGrid
              columns={payoffColumns}
              rows={loanPayoffs}
              frameworkComponents={frameworkComponents}
              domLayout="autoHeight"
            />
          </Grid>
        </Grid>
      </Paper >
      <RetractPayoffDialog loanId={loanId} loanPayoffDataId={currentPayoff?.loanPayoffDataId} />
      <PayoffDetailsDialog loanId={loanId} />
    </>
  );
});